import { type ReactNode, useId } from 'react';
import { type FieldPath, type FieldValues, useController, useFormContext } from 'react-hook-form';
import { useRegisterField } from './fieldRegistry';

/**
 * Общее для полей форм, привязанных к react-hook-form (README «Оверлеи → Поля форм»).
 * Имя поля = поле контракта (snake_case), поэтому ошибки 422 подставляются без переименования.
 */
export interface BaseFieldProps<TValues extends FieldValues> {
  name: FieldPath<TValues>;
  label: ReactNode;
  required?: boolean;
  hint?: ReactNode;
  wide?: boolean;
  disabled?: boolean;
}

export function useFieldIds() {
  const id = useId();
  return { id, errorId: `${id}-error`, hintId: `${id}-hint`, labelId: `${id}-label` };
}

export function useBoundField<TValues extends FieldValues>(name: FieldPath<TValues>) {
  const { control } = useFormContext<TValues>();
  useRegisterField(name);
  return useController({ name, control });
}

export function describedBy(
  hasError: boolean,
  hasHint: boolean,
  ids: { errorId: string; hintId: string },
) {
  if (hasError) return ids.errorId;
  return hasHint ? ids.hintId : undefined;
}

export function asText(value: unknown): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  return '';
}

export function asList(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string')
    : [];
}
